import { useCallback } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { Tooltip, Input, Button } from 'antd'
import { FormItem, FormRowWithValidation } from 'admin/lib/Forms'
import { DeleteOutlined } from '@ant-design/icons'
import {
  AddressName,
  KnownAddressModalLink
} from 'admin/pages/RecordEdit/common/KnownAddressModalLink'
import { CheckKnownAddress } from 'admin/lib/SimpleDataValidation'

export const KnownAddressInput = ({
  form,
  addressName,
  knownlabel,
  knownPropertyPath,
  givenPropertyPath,
  enteredPropertyPath,
  coordinatesPropertyPath,
  projectCountry,
  disabled = false,
  hidden = false,
  instrumented = false
}: {
  form: UseFormReturn
  addressName: AddressName
  knownlabel: string
  knownPropertyPath: string
  givenPropertyPath: string
  enteredPropertyPath: string
  coordinatesPropertyPath: string
  projectCountry: string
  disabled?: boolean
  hidden?: boolean
  instrumented?: boolean
}) => {
  const checkFieldIsDirtyByPath = useCallback(
    // dirtyFields is object, e.g. { parent: { parentProp: true, child: { childProp: true } }
    (path: string) => {
      return (
        path
          .split('.')
          .reduce(
            (currentObj, pathPart) =>
              currentObj ? currentObj[pathPart] : undefined,
            form.formState.dirtyFields as
              | Record<string, any>
              | boolean
              | undefined
          ) === true
      )
    },
    [form.formState.dirtyFields]
  )

  return (
    <FormRowWithValidation>
      <FormItem
        label={knownlabel}
        name={knownPropertyPath}
        id={`data${addressName.replace(' ', '')}AddressKnown`}
        simpleDataValidationRules={v =>
          CheckKnownAddress(v, form.getValues(givenPropertyPath))
        }
        hidden={hidden}
        instrumented={instrumented}
        contextLink={
          <KnownAddressModalLink
            addressName={addressName}
            projectCountry={projectCountry}
            getCurrentValues={() => ({
              given: form.getValues(givenPropertyPath),
              known: form.getValues(knownPropertyPath)
            })}
            getGivenIsDirty={() => checkFieldIsDirtyByPath(givenPropertyPath)}
            onSubmit={({ entered, known, coordinates }) => {
              form.setValue(enteredPropertyPath, entered, {
                shouldValidate: true,
                shouldDirty: true
              })
              form.setValue(knownPropertyPath, known, {
                shouldValidate: true,
                shouldDirty: true
              })
              form.setValue(coordinatesPropertyPath, coordinates, {
                shouldValidate: true,
                shouldDirty: true
              })
            }}
          />
        }
      >
        <Input.TextArea autoSize rows={1} disabled />
      </FormItem>
      <Tooltip title="Remove Address">
        <Button
          aria-label={`Remove Known ${addressName} Address`}
          icon={<DeleteOutlined />}
          danger
          onClick={() => {
            form.setValue(enteredPropertyPath, null, {
              shouldValidate: false,
              shouldDirty: true
            })
            form.setValue(knownPropertyPath, null, {
              shouldValidate: false,
              shouldDirty: true
            })
            form.setValue(coordinatesPropertyPath, null, {
              shouldValidate: false,
              shouldDirty: true
            })
          }}
          hidden={hidden}
          style={{ marginTop: '1.5rem' }}
        />
      </Tooltip>
    </FormRowWithValidation>
  )
}
